"use client";

import type * as React from "react";
import { format, parseISO } from "date-fns";
import { useSearchParams } from "next/navigation";
import { parseBookingSearchParams } from "@/lib/booking/search-params";
import { useBookingLocations } from "@/lib/hooks/use-booking-locations";
import { formatTimeLabel } from "@/lib/booking/time-slots";

function formatParamDate(date: string): string {
  return format(parseISO(date), "dd MMM");
}

export const BookingSearchSummary: React.FC = () => {
  const searchParams = useSearchParams();
  const booking = parseBookingSearchParams(searchParams);
  const { getLocationLabel, isLoading } = useBookingLocations();

  if (!booking) {
    return (
      <>
        <p className="text-gray-900 text-sm">Select pickup and dates</p>
        <p className="text-gray-900 text-sm">to see your search</p>
      </>
    );
  }

  const pickupLabel = getLocationLabel(booking.pickup);
  const returnLabel = booking.return
    ? getLocationLabel(booking.return)
    : pickupLabel;
  const needsDistinctReturn =
    Boolean(booking.return) && booking.return !== booking.pickup;
  const hasLocationLabels =
    Boolean(pickupLabel) &&
    (!needsDistinctReturn || Boolean(returnLabel));
  const pickupTimeLabel =
    booking.pickupTime !== undefined
      ? formatTimeLabel(booking.pickupTime)
      : "";
  const returnTimeLabel =
    booking.returnTime !== undefined
      ? formatTimeLabel(booking.returnTime)
      : "";
  const locationLine = hasLocationLabels
    ? needsDistinctReturn
      ? `${pickupLabel} → ${returnLabel}`
      : pickupLabel
    : needsDistinctReturn
      ? `${booking.pickup} → ${booking.return}`
      : booking.pickup;

  const dateLine = `${formatParamDate(booking.pickupDate)}${pickupTimeLabel ? ` ${pickupTimeLabel}` : ""} - ${formatParamDate(booking.returnDate)}${returnTimeLabel ? ` ${returnTimeLabel}` : ""}`;

  return (
    <>
      <p className="text-gray-900 text-sm">
        {hasLocationLabels || !isLoading ? (
          <b>{locationLine}</b>
        ) : (
          <span
            className="inline-block h-4 w-40 animate-pulse rounded bg-gray-200"
            aria-hidden="true"
          />
        )}
      </p>
      <p className="text-gray-900 text-sm">{dateLine}</p>
    </>
  );
};
